import React from "react";
import Layout from "../components/Layout";
import AdminNavBar from "../components/AdminNavBar";

type ProductStatus = "DRAFT" | "ACTIVE" | "INACTIVE" | "ARCHIVED";

interface ProductSummary {
  id: number;
  name: string;
  category?: string | null;
  status: ProductStatus;
  shortDescription?: string | null;
  coasCount?: number | null;
  imagesCount?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface ProductImageForm {
  id?: number;
  url: string; // internal/legacy URL, e.g. /images/products/... or Nextcloud URL
  publicUrl?: string; // full public URL for email/preview
  caption: string;
  sortOrder: number;
  isUploading?: boolean;
  uploadError?: string | null;
}

interface ProductFormValues {
  name: string;
  category: string;
  status: ProductStatus;
  shortDescription: string;
  longDescription: string;
  story: string;
  linkedCoaSampleIds: number[];
  images: ProductImageForm[];
}

interface CoaSampleOption {
  id: number; // CoaSample.id
  sampleId: string;
  sampleName?: string | null;
}

interface ProductImageResponse {
  id: number;
  url: string;
  publicUrl?: string | null;
  caption?: string | null;
  sortOrder: number;
}

interface ProductDetailResponse {
  id: number;
  name: string;
  category?: string | null;
  status: ProductStatus;
  shortDescription?: string | null;
  longDescription?: string | null;
  story?: string | null;
  linkedCoas?: {
    productId: number;
    sampleId: number;
    sortOrder: number;
    sample: {
      id: number;
      sampleId: string;
      sampleName?: string | null;
    };
  }[];
  images?: ProductImageResponse[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

const baseUrl = process.env.REACT_APP_API_URL || "";

// Turn stored URL into something the browser can load:
// - Nextcloud URLs → backend proxy
// - /images/...   → backend static
// - http(s) URLs  → as-is
// - anything else → treat as relative to backend
function resolveImageUrl(url: string): string {
  if (!url) return "";

  if (url.includes("nextcloud.farmtocode.com")) {
    return `${baseUrl}/api/coa/file?url=${encodeURIComponent(url)}`;
  }

  if (url.startsWith("/images/")) {
    return `${baseUrl}${url}`;
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  return `${baseUrl}/${url.replace(/^\/+/, "")}`;
}

const ManageProducts: React.FC = () => {
  const [products, setProducts] = React.useState<ProductSummary[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  const [selectedProductId, setSelectedProductId] =
    React.useState<number | null>(null);
  const [isCreating, setIsCreating] = React.useState<boolean>(false);

  const [formValues, setFormValues] = React.useState<ProductFormValues>({
    name: "",
    category: "",
    status: "DRAFT",
    shortDescription: "",
    longDescription: "",
    story: "",
    linkedCoaSampleIds: [],
    images: [],
  });

  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const [coaOptions, setCoaOptions] = React.useState<CoaSampleOption[]>([]);
  const [coaError, setCoaError] = React.useState<string | null>(null);

  React.useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      setCoaError(null);

      // Products
      try {
        const res = await fetch(`${baseUrl}/api/products`);

        if (!res.ok) {
          const text = await res.text();
          console.error("Products error body:", text);
          throw new Error(`Failed to load products: ${res.status}`);
        }

        const json = await res.json();
        const items: ProductSummary[] = Array.isArray(json)
          ? json
          : ((json.items as ProductSummary[]) || []);

        setProducts(items);
      } catch (err: unknown) {
        console.error("Error loading products:", err);
        const msg =
          err instanceof Error ? err.message : "Failed to load products";
        setError(msg);
      } finally {
        setLoading(false);
      }

      // COA options
      try {
        const resCoa = await fetch(`${baseUrl}/api/coa/samples`);

        if (!resCoa.ok) {
          const text = await resCoa.text();
          console.error("COA samples error body:", text);
          throw new Error(`Failed to load COA samples: ${resCoa.status}`);
        }

        const coaJson = await resCoa.json();
        const samples: CoaSampleOption[] = (coaJson as any[]).map((s) => ({
          id: s.id,
          sampleId: s.sampleId,
          sampleName: s.sampleName,
        }));

        setCoaOptions(samples);
      } catch (err: unknown) {
        console.error("Error loading COA options:", err);
        const msg =
          err instanceof Error ? err.message : "Failed to load COA options";
        setCoaError(msg);
      }
    }

    loadData();
  }, []);

  function resetForm() {
    setFormValues({
      name: "",
      category: "",
      status: "DRAFT",
      shortDescription: "",
      longDescription: "",
      story: "",
      linkedCoaSampleIds: [],
      images: [],
    });
    setSaveError(null);
    setDetailError(null);
  }

  function handleCreateClick() {
    setIsCreating(true);
    setSelectedProductId(null);
    resetForm();
  }

  async function handleEditClick(productId: number) {
    setIsCreating(false);
    setSelectedProductId(productId);
    setSaveError(null);
    setDetailError(null);
    setDetailLoading(true);

    try {
      const res = await fetch(`${baseUrl}/api/products/${productId}`);

      if (!res.ok) {
        const text = await res.text();
        console.error("Product detail error body:", text);
        throw new Error(`Failed to load product: ${res.status}`);
      }

      const data: ProductDetailResponse = await res.json();

      const linkedIds =
        Array.isArray(data.linkedCoas) && data.linkedCoas.length > 0
          ? data.linkedCoas.map((c) => c.sampleId)
          : [];

      const images: ProductImageForm[] = Array.isArray(data.images)
        ? data.images
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((img) => ({
              id: img.id,
              url: img.url,
              publicUrl: img.publicUrl || undefined,
              caption: img.caption || "",
              sortOrder: img.sortOrder ?? 0,
              isUploading: false,
              uploadError: null,
            }))
        : [];

      setFormValues({
        name: data.name || "",
        category: data.category || "",
        status: data.status || "DRAFT",
        shortDescription: data.shortDescription || "",
        longDescription: data.longDescription || "",
        story: data.story || "",
        linkedCoaSampleIds: linkedIds,
        images,
      });
    } catch (err: unknown) {
      console.error("Error loading product detail:", err);
      const msg =
        err instanceof Error ? err.message : "Failed to load product detail";
      setDetailError(msg);
    } finally {
      setDetailLoading(false);
    }
  }

  function toggleCoaInForm(sampleId: number) {
    const selectedCoa = coaOptions.find((c) => c.id === sampleId);

    setFormValues((prev) => {
      const already = prev.linkedCoaSampleIds.includes(sampleId);

      const nextIds = already
        ? prev.linkedCoaSampleIds.filter((id) => id !== sampleId)
        : [...prev.linkedCoaSampleIds, sampleId];

      let nextName = prev.name;

      if (!already && prev.name.trim() === "" && selectedCoa) {
        nextName = selectedCoa.sampleName?.trim() || selectedCoa.sampleId;
      }

      return {
        ...prev,
        linkedCoaSampleIds: nextIds,
        name: nextName,
      };
    });
  }

  function addImageRow() {
    setFormValues((prev) => {
      const nextOrder =
        prev.images.length === 0
          ? 0
          : Math.max(...prev.images.map((i) => i.sortOrder)) + 1;

      return {
        ...prev,
        images: [
          ...prev.images,
          {
            url: "",
            publicUrl: undefined,
            caption: "",
            sortOrder: nextOrder,
            isUploading: false,
            uploadError: null,
          },
        ],
      };
    });
  }

  function updateImageRow(index: number, patch: Partial<ProductImageForm>) {
    setFormValues((prev) => {
      const images = [...prev.images];
      images[index] = { ...images[index], ...patch };
      return { ...prev, images };
    });
  }

  function removeImageRow(index: number) {
    setFormValues((prev) => {
      const images = prev.images.filter((_, i) => i !== index);
      return { ...prev, images };
    });
  }

  async function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);

    if (!formValues.name.trim()) {
      setSaveError("Name is required.");
      setSaving(false);
      return;
    }

    try {
      const payload = {
        name: formValues.name.trim(),
        category: formValues.category.trim() || null,
        status: formValues.status,
        shortDescription: formValues.shortDescription.trim() || null,
        longDescription: formValues.longDescription.trim() || null,
        story: formValues.story.trim() || null,
        coaSampleIds: formValues.linkedCoaSampleIds,
        images: formValues.images
          .filter((img) => img.url.trim() !== "")
          .map((img) => ({
            url: img.url.trim(),
            publicUrl: img.publicUrl?.trim() || null,
            caption: img.caption.trim() || null,
            sortOrder: img.sortOrder ?? 0,
          })),
      };

      const res = await fetch(`${baseUrl}/api/products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Create product error:", res.status, text);
        throw new Error(`Create failed: ${res.status}`);
      }

      const created: ProductDetailResponse & { coaSamples?: any[] } =
        await res.json();

      const summary: ProductSummary = {
        id: created.id,
        name: created.name,
        category: created.category,
        status: created.status,
        shortDescription: created.shortDescription,
        coasCount: Array.isArray(created.coaSamples)
          ? created.coaSamples.length
          : Array.isArray(created.linkedCoas)
          ? created.linkedCoas.length
          : 0,
        imagesCount: Array.isArray(created.images)
          ? created.images.length
          : 0,
        createdAt: created.createdAt,
        updatedAt: created.updatedAt,
      };

      setProducts((prev) => [summary, ...prev]);
      setIsCreating(false);
      resetForm();
    } catch (err: unknown) {
      console.error("Failed to create product:", err);
      const msg =
        err instanceof Error ? err.message : "Failed to create product";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (selectedProductId == null) return;

    setSaving(true);
    setSaveError(null);

    if (!formValues.name.trim()) {
      setSaveError("Name is required.");
      setSaving(false);
      return;
    }

    try {
      const payload = {
        name: formValues.name.trim(),
        category: formValues.category.trim() || null,
        status: formValues.status,
        shortDescription: formValues.shortDescription.trim() || null,
        longDescription: formValues.longDescription.trim() || null,
        story: formValues.story.trim() || null,
        coaSampleIds: formValues.linkedCoaSampleIds,
        images: formValues.images
          .filter((img) => img.url.trim() !== "")
          .map((img) => ({
            url: img.url.trim(),
            publicUrl: img.publicUrl?.trim() || null,
            caption: img.caption.trim() || null,
            sortOrder: img.sortOrder ?? 0,
          })),
      };

      const res = await fetch(`${baseUrl}/api/products/${selectedProductId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Update product error:", res.status, text);
        throw new Error(`Update failed: ${res.status}`);
      }

      const updated: ProductDetailResponse & { coaSamples?: any[] } =
        await res.json();

      const summary: ProductSummary = {
        id: updated.id,
        name: updated.name,
        category: updated.category,
        status: updated.status,
        shortDescription: updated.shortDescription,
        coasCount: Array.isArray(updated.coaSamples)
          ? updated.coaSamples.length
          : Array.isArray(updated.linkedCoas)
          ? updated.linkedCoas.length
          : 0,
        imagesCount: Array.isArray(updated.images)
          ? updated.images.length
          : 0,
        createdAt: updated.createdAt,
        updatedAt: updated.updatedAt,
      };

      setProducts((prev) =>
        prev.map((p) => (p.id === summary.id ? summary : p))
      );
    } catch (err: unknown) {
      console.error("Failed to update product:", err);
      const msg =
        err instanceof Error ? err.message : "Failed to update product";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (selectedProductId == null) return;
    const confirmed = window.confirm(
      "Delete this product? This cannot be undone."
    );
    if (!confirmed) return;

    setDeleting(true);
    setSaveError(null);

    try {
      const res = await fetch(`${baseUrl}/api/products/${selectedProductId}`, {
        method: "DELETE",
      });

      if (!res.ok && res.status !== 204) {
        const text = await res.text();
        console.error("Delete product error:", res.status, text);
        throw new Error(`Delete failed: ${res.status}`);
      }

      setProducts((prev) => prev.filter((p) => p.id !== selectedProductId));
      setSelectedProductId(null);
      resetForm();
    } catch (err: unknown) {
      console.error("Failed to delete product:", err);
      const msg =
        err instanceof Error ? err.message : "Failed to delete product";
      setSaveError(msg);
    } finally {
      setDeleting(false);
    }
  }

  function formatDate(value?: string | null): string {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString();
  }

  return (
    <Layout>
      <AdminNavBar />
      <div className="pt-[110px] min-h-screen bg-fractal-pattern-3 bg-cover bg-fixed p-10 relative">
        <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-10">
          Manage Products
        </h1>

        <div className="max-w-6xl mx-auto bg-white/80 backdrop-blur-md shadow-xl rounded-xl p-6 border border-gray-200">
          {/* Header / actions */}
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-700 mb-1">
                Products
              </h2>
              <p className="text-sm text-gray-600">
                Products are the marketing layer built on top of COAs. Link COA
                samples and add images + descriptions here.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded-md bg-green-600 text-white text-sm hover:bg-green-700"
                onClick={handleCreateClick}
              >
                + New Product
              </button>
            </div>
          </div>

          {/* Main grid: left table, right panel */}
          <div className="grid grid-cols-1 lg:grid-cols-[2fr,1.1fr] gap-6">
            {/* LEFT: Products table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                {loading ? (
                  <p className="text-sm text-gray-500">Loading products…</p>
                ) : error ? (
                  <p className="text-sm text-red-600">
                    Error loading products: {error}
                  </p>
                ) : products.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No products yet. Click &ldquo;New Product&rdquo; to create
                    one.
                  </p>
                ) : (
                  <p className="text-sm text-gray-700">
                    {products.length} product
                    {products.length !== 1 ? "s" : ""} found.
                  </p>
                )}
              </div>

              {!loading && !error && products.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <th className="px-4 py-2">Name</th>
                        <th className="px-4 py-2">Category</th>
                        <th className="px-4 py-2">Status</th>
                        <th className="px-4 py-2 text-center"># COAs</th>
                        <th className="px-4 py-2 text-center"># Images</th>
                        <th className="px-4 py-2">Created</th>
                        <th className="px-4 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {products.map((p) => (
                        <tr
                          key={p.id}
                          className={
                            selectedProductId === p.id
                              ? "bg-emerald-50/70"
                              : ""
                          }
                        >
                          <td className="px-4 py-2 whitespace-nowrap text-gray-900">
                            <div className="font-medium">{p.name}</div>
                            {p.shortDescription && (
                              <div className="text-xs text-gray-500 truncate max-w-xs">
                                {p.shortDescription}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                            {p.category || "—"}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                p.status === "ACTIVE"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : p.status === "DRAFT"
                                  ? "bg-amber-100 text-amber-800"
                                  : p.status === "INACTIVE"
                                  ? "bg-gray-100 text-gray-700"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {p.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-center text-gray-700">
                            {p.coasCount ?? 0}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-center text-gray-700">
                            {p.imagesCount ?? 0}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-gray-500">
                            {formatDate(p.createdAt)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-right">
                            <button
                              type="button"
                              className="text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                              onClick={() => handleEditClick(p.id)}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* RIGHT: Create / Edit panel */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              {isCreating ? (
                <form onSubmit={handleCreateSubmit} className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    New Product
                  </h3>
                  <p className="text-xs text-gray-500 mb-2">
                    Basic product details, linked COAs, and marketing images.
                  </p>

                  {saveError && (
                    <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded">
                      {saveError}
                    </div>
                  )}

                  <ProductFields
                    formValues={formValues}
                    setFormValues={setFormValues}
                    coaOptions={coaOptions}
                    coaError={coaError}
                    toggleCoaInForm={toggleCoaInForm}
                    addImageRow={addImageRow}
                    updateImageRow={updateImageRow}
                    removeImageRow={removeImageRow}
                    productId={null}
                  />

                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-60"
                      disabled={saving}
                    >
                      {saving ? "Creating…" : "Create Product"}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-md border border-gray-300 text-sm"
                      disabled={saving}
                      onClick={() => {
                        setIsCreating(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : selectedProductId != null ? (
                detailLoading ? (
                  <p className="text-sm text-gray-500">Loading product…</p>
                ) : detailError ? (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      Edit Product
                    </h3>
                    <p className="text-sm text-red-600 mb-2">{detailError}</p>
                    <button
                      type="button"
                      className="px-3 py-1 rounded-md border border-gray-300 text-sm"
                      onClick={() => handleEditClick(selectedProductId)}
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleUpdateSubmit} className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Edit Product
                    </h3>
                    <p className="text-xs text-gray-500 mb-2">
                      Editing product ID{" "}
                      <span className="font-mono">{selectedProductId}</span>.
                      Adjust details, linked COAs, and images here.
                    </p>

                    {saveError && (
                      <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded">
                        {saveError}
                      </div>
                    )}

                    <ProductFields
                      formValues={formValues}
                      setFormValues={setFormValues}
                      coaOptions={coaOptions}
                      coaError={coaError}
                      toggleCoaInForm={toggleCoaInForm}
                      addImageRow={addImageRow}
                      updateImageRow={updateImageRow}
                      removeImageRow={removeImageRow}
                      productId={selectedProductId}
                    />

                    <div className="flex gap-3 pt-2">
                      <button
                        type="submit"
                        className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-60"
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save Changes"}
                      </button>
                      <button
                        type="button"
                        className="px-4 py-2 rounded-md border border-gray-300 text-sm"
                        disabled={saving}
                        onClick={() => {
                          setSelectedProductId(null);
                          resetForm();
                        }}
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        className="ml-auto px-4 py-2 rounded-md border border-red-300 text-sm text-red-700 hover:bg-red-50 disabled:opacity-60"
                        disabled={deleting}
                        onClick={handleDelete}
                      >
                        {deleting ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </form>
                )
              ) : (
                <div className="h-full flex items-center justify-center text-center">
                  <p className="text-sm text-gray-400">
                    Select a product on the left to edit, or click{" "}
                    <span className="font-medium text-gray-600">
                      &ldquo;New Product&rdquo;
                    </span>{" "}
                    to create one.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

interface ProductFieldsProps {
  formValues: ProductFormValues;
  setFormValues: React.Dispatch<React.SetStateAction<ProductFormValues>>;
  coaOptions: CoaSampleOption[];
  coaError: string | null;
  toggleCoaInForm: (sampleId: number) => void;
  addImageRow: () => void;
  updateImageRow: (index: number, patch: Partial<ProductImageForm>) => void;
  removeImageRow: (index: number) => void;
  productId: number | null;
}

const ProductFields: React.FC<ProductFieldsProps> = ({
  formValues,
  setFormValues,
  coaOptions,
  coaError,
  toggleCoaInForm,
  addImageRow,
  updateImageRow,
  removeImageRow,
  productId,
}) => {
  async function handleImageFileChange(
    index: number,
    file: File | null | undefined
  ) {
    if (!file) return;

    if (!productId) {
      updateImageRow(index, {
        isUploading: false,
        uploadError: "Save the product first, then upload images.",
      });
      return;
    }

    updateImageRow(index, { isUploading: true, uploadError: null });

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("productId", String(productId));
      formData.append("caption", formValues.images[index]?.caption || "");
      formData.append(
        "sortOrder",
        String(formValues.images[index]?.sortOrder ?? index)
      );

      const res = await fetch(`${baseUrl}/api/products/images/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Product image upload error:", res.status, text);
        throw new Error(`Upload failed: ${res.status}`);
      }

      const json = await res.json();

      const image = json.image as {
        url?: string;
        publicUrl?: string;
        caption?: string | null;
        sortOrder?: number;
      };

      const storedUrl = image?.url || "";
      const publicUrl = image?.publicUrl || json.url;

      updateImageRow(index, {
        url: storedUrl || publicUrl || "",
        publicUrl: publicUrl || undefined,
        isUploading: false,
        uploadError: null,
      });
    } catch (err: any) {
      console.error("Failed to upload product image:", err);
      updateImageRow(index, {
        isUploading: false,
        uploadError: err?.message || "Upload failed",
      });
    }
  }

  return (
    <>
      {/* Basic fields */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name<span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          value={formValues.name}
          onChange={(e) =>
            setFormValues((prev) => ({
              ...prev,
              name: e.target.value,
            }))
          }
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Category
        </label>
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          value={formValues.category}
          placeholder="e.g. Rosin, Gummies, Tincture"
          onChange={(e) =>
            setFormValues((prev) => ({
              ...prev,
              category: e.target.value,
            }))
          }
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Status
        </label>
        <select
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          value={formValues.status}
          onChange={(e) =>
            setFormValues((prev) => ({
              ...prev,
              status: e.target.value as ProductStatus,
            }))
          }
        >
          <option value="DRAFT">Draft</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="ARCHIVED">Archived</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Short Description
        </label>
        <textarea
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          rows={2}
          value={formValues.shortDescription}
          onChange={(e) =>
            setFormValues((prev) => ({
              ...prev,
              shortDescription: e.target.value,
            }))
          }
          placeholder="One or two lines for emails / cards"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Long Description
        </label>
        <textarea
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          rows={3}
          value={formValues.longDescription}
          onChange={(e) =>
            setFormValues((prev) => ({
              ...prev,
              longDescription: e.target.value,
            }))
          }
          placeholder="Full product description for website / detail pages"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Story / Notes
        </label>
        <textarea
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          rows={3}
          value={formValues.story}
          onChange={(e) =>
            setFormValues((prev) => ({
              ...prev,
              story: e.target.value,
            }))
          }
          placeholder="Grower notes, farm story, strain background, etc."
        />
      </div>

      {/* Linked COAs */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            Linked COA Samples (optional)
          </label>
          {coaError && (
            <span className="text-xs text-red-500">{coaError}</span>
          )}
        </div>
        <div className="border border-gray-200 rounded-md max-h-40 overflow-auto px-2 py-2 text-sm bg-gray-50">
          {coaOptions.length === 0 ? (
            <p className="text-xs text-gray-500">
              No COA samples available yet. Upload COAs on the COA page first.
            </p>
          ) : (
            coaOptions.map((coa) => (
              <label key={coa.id} className="flex items-center gap-2 py-1">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={formValues.linkedCoaSampleIds.includes(coa.id)}
                  onChange={() => toggleCoaInForm(coa.id)}
                />
                <span>
                  {coa.sampleName || "Unnamed sample"}{" "}
                  <span className="text-xs text-gray-400">
                    ({coa.sampleId})
                  </span>
                </span>
              </label>
            ))
          )}
        </div>
      </div>

      {/* Images */}
      <div className="border-t border-gray-200 pt-3 mt-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-800">
            Marketing Images
          </h4>
          <button
            type="button"
            className="text-xs px-2 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
            onClick={addImageRow}
          >
            + Add image
          </button>
        </div>

        {formValues.images.length === 0 ? (
          <p className="text-xs text-gray-500">
            No images yet. Click &ldquo;Add image&rdquo; to attach one.
          </p>
        ) : (
          <div className="space-y-3">
            {formValues.images.map((img, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-md p-2 bg-gray-50"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-600">
                    Image #{index + 1}
                  </span>
                  <button
                    type="button"
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => removeImageRow(index)}
                  >
                    Remove
                  </button>
                </div>

                <div className="space-y-2">
                  {/* Upload control */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Upload / replace image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      className="w-full text-xs"
                      onChange={(e) =>
                        handleImageFileChange(
                          index,
                          e.target.files ? e.target.files[0] : null
                        )
                      }
                    />
                    {img.isUploading && (
                      <p className="text-[11px] text-gray-500 mt-1">
                        Uploading…
                      </p>
                    )}
                    {img.uploadError && (
                      <p className="text-[11px] text-red-600 mt-1">
                        {img.uploadError}
                      </p>
                    )}
                  </div>

                  {/* Stored URL (read-only) */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Stored URL
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-[11px] bg-gray-100"
                      value={img.url}
                      readOnly
                    />
                  </div>

                  {/* Caption */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Caption (optional)
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs"
                      value={img.caption}
                      onChange={(e) =>
                        updateImageRow(index, { caption: e.target.value })
                      }
                      placeholder="If desired, add a caption to show with this image"
                    />
                  </div>

                  {/* Sort + preview */}
                  <div className="flex items-center gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Sort order
                      </label>
                      <input
                        type="number"
                        className="w-24 border border-gray-300 rounded-md px-2 py-1 text-xs"
                        value={img.sortOrder}
                        onChange={(e) =>
                          updateImageRow(index, {
                            sortOrder: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </div>

                    {(img.publicUrl || img.url) && (
                      <div className="ml-2">
                        <span className="block text-xs text-gray-500 mb-1">
                          Preview
                        </span>
                        <img
                          src={resolveImageUrl(img.publicUrl || img.url)}
                          alt={img.caption || `Product image ${index + 1}`}
                          className="h-16 w-16 object-cover rounded border border-gray-200"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ManageProducts;
